import { Plus, X, CheckCircle } from "lucide-react";
import { ListChecks, Prohibit, Lightning } from "../../PhosphorIcons";
import { CategoryIcon } from "../../components/CategoryIcon";
import { S as S_ } from "../../theme/sharedStyles";
import { InlineCategoryCreator } from "../../components/InlineCategoryCreator";
import { EmojiPicker } from "../../components/pickers/EmojiPicker";
import { HabitIconPicker } from "../../components/pickers/IconPickers";
import { suggestEmojiFromName } from "../../utils/emojiSuggest";
import { now, daysInMonth, todayStr } from "../../utils/dateHelpers";
import { IntervalPicker } from "../../components/pickers/IntervalPicker";
import { limitedOffDays, isHabitCountedYearly, isHabitHiddenFromDaily, isCountFrequency, hasExcludableDates, toggleExcludedDate } from "../../utils/habitLogic";
import { HabitCheckbox } from "../../components/HabitCheckbox";
import { GoalNewToggle } from "../../components/GoalNewToggle";
import { shapeOf, goalFieldChanged, goalPreview } from "../../utils/habitWeeklyGoal";
import { HABIT_UNITS, customPreset, unitOf, unitDef, defaultGoalFor, COUNT_MAX } from "../../utils/habitUnits";
import { UnitPicker } from "../../components/pickers/UnitPicker";
import { categoryInk, isDarkMode } from "../../theme/colorEngine";
import { stopAlarmSound } from "../../utils/alarmSound";
import { DEFAULT_ALARM_SOUND } from "../../constants/misc";
import { ReminderPicker } from "../../components/pickers/ReminderPicker";
import { NativePickerInput } from "../../components/NativePickerInput";
import { Capacitor } from "@capacitor/core";

// Panel surfaces sit on a pastel theme-tinted background now (see HabitsTab's
// modal container), so inputs/buttons drop their border lines in favour of a
// soft shadow to read as distinct white surfaces.
const S = {
  ...S_,
  input: { ...S_.input, border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.08)" },
  btn:   { ...S_.btn,   border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" },
  label: { ...S_.label, color: "var(--c-text-accent)" },
};

export function renderHabitForm(f, setF, onSave, onCancel, saveLabel, categories, catCreator = {}, saving = false) {
    const { show: showNewCat, setShow: setShowNewCat, setCategories, savedColors, addSavedColor } = catCreator;
    const isTodo = (f.habitType || "todo") === "todo";
    // "Start a new goal" switch — only for habits that already have a past (the
    // edit form sets _goalNote to the badge text; new habits never get one).
    // Under the count stepper when the habit is counted, else under Times per day.
    // Hidden once the type itself is switched — that's always a correction.
    // Shown only once the user has actually changed that number / those days
    // (and hidden again if they put it back). One switch for Times per day
    // ("daily"), one for the frequency pickers ("freq"), so each can be a new
    // goal or a correction on its own.
    const goalToggleFor = (group, fields) => {
      if (!f._goalNote || (f._goalShape && shapeOf(f) !== f._goalShape)) return null;
      if (!fields.some(k => goalFieldChanged(f, k))) return null;
      const key = group === "daily" ? "_goalNewDaily" : "_goalNewFreq";
      const nowKey = group === "daily" ? "_goalNowDaily" : "_goalNowFreq";
      const field = fields.find(k => goalFieldChanged(f, k));
      return <GoalNewToggle on={!!f[key]} onChange={(v) => setF(p => ({ ...p, [key]: v }))} info={f._goalNote}
        now={!!f[nowKey]} onNow={(v) => setF(p => ({ ...p, [nowKey]: v }))} nextLabel={f._goalNextFrom}
        preview={goalPreview(f, field, !!f[nowKey])} />;
    };
    const isRestricted = f.habitType === "limited" || f.habitType === "nottodo";
    // How many Time of Day slots this habit can have picked at once — tied to
    // "times per day" (todo habits only; restricted habits are single-select).
    // Only a Count habit is done several times a day; an amount (steps, pages,
    // glasses, time, custom) is one daily total, so it sits in one slot.
    const unit = unitOf(f);
    const maxTimeOfDay = isTodo && unit === "count" ? Math.max(1, f.dailyCount || 1) : 1;
    // Dark mode: lighter tints so the type labels read bright (Jade, 2026-10-17).
    const dk = isDarkMode();
    const TODO_TINT = dk ? "#6FD394" : "#1E8A45", RESTR_TINT = dk ? "#FFA25C" : "#D35F00";
    const IDLE = dk ? "#b4b4bc" : "#8a8a90", IDLE_SOFT = dk ? "#a4a4ac" : "#9a9aa0";
    return (
    <>
      {/* ── Top-level: To Do vs Restricted ──
          A switcher (Jade, 2026-10-11): a soft track with one white pill that
          slides to the picked side, iOS segmented-control style. No coloured
          fills; the picked side is just bolder/darker. Ease-out, no overshoot. */}
      <div data-tour="habit-type-toggle" role="radiogroup" aria-label="Habit type"
        style={{ position: "relative", display: "flex", marginBottom: 14, padding: 4, borderRadius: 16, background: "rgba(120,120,128,0.12)" }}>
        <div aria-hidden style={{
          position: "absolute", top: 4, bottom: 4, left: 4, width: "calc(50% - 4px)", borderRadius: 12,
          background: "var(--c-bg-card)", boxShadow: "0 2px 8px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)",
          transform: isRestricted ? "translateX(100%)" : "translateX(0)",
          transition: "transform 0.28s cubic-bezier(0.22,1,0.36,1)",
        }} />
        {[
          { on: isTodo, Icon: ListChecks, label: "To Do", desc: "Build & maintain", tint: TODO_TINT,
            pick: () => setF(p => ({ ...p, habitType: "todo" })) },
          { on: isRestricted, Icon: Prohibit, label: "Restricted", desc: "Cut back or quit", tint: RESTR_TINT,
            pick: () => setF(p => ({ ...p, habitType: p.habitType === "todo" || !p.habitType ? "nottodo" : p.habitType })) },
        ].map(({ on, Icon, label, desc, tint, pick }) => (
          <button key={label} role="radio" aria-checked={on} onClick={pick}
            style={{
              position: "relative", zIndex: 1, flex: 1, padding: "10px 8px", borderRadius: 12, border: "none",
              background: "transparent", cursor: "pointer", textAlign: "center",
            }}>
            <Icon size={20} weight={on ? "bold" : "regular"} color={on ? tint : IDLE} style={{ transition: "color 0.2s" }} />
            <div style={{ fontSize: 14.5, fontWeight: 700, color: on ? tint : IDLE, marginTop: 4, transition: "color 0.2s" }}>{label}</div>
            <div style={{ fontSize: 12, color: on ? tint : IDLE_SOFT, marginTop: 2, transition: "color 0.2s" }}>{desc}</div>
          </button>
        ))}
      </div>

      {/* ── If Restricted: restriction type as a grouped list ──
          (Jade, 2026-10-15) Two side-by-side buttons under the To Do / Restricted
          switcher read as a second switcher. A sub-choice reads better as a
          settings-style list: one card, two rows split by a hairline, a radio
          on the right. No orange outline; only the radio and icon show the pick. */}
      {isRestricted && (
        <div style={{ marginBottom: 14 }}>
          <p style={{ ...S.label, marginBottom: 6, paddingLeft: 4 }}>RESTRICTION TYPE</p>
          <div role="radiogroup" aria-label="Restriction type" style={{ background: "var(--c-bg-card)", borderRadius: 14, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
            {[
              { key: "nottodo", Icon: Prohibit,  label: "Full Stop", desc: "Eliminate entirely" },
              { key: "limited", Icon: Lightning, label: "Limited",   desc: "Allow with limits"  },
            ].map(({ key, Icon, label, desc }, i) => {
              const on = f.habitType === key;
              return (
                <button key={key} type="button" role="radio" aria-checked={on} onClick={() => setF(p => ({ ...p, habitType: key }))}
                  style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "11px 14px", border: "none", borderTop: i ? "1px solid rgba(120,120,128,0.16)" : "none", background: "transparent", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                  <Icon size={20} weight={on ? "bold" : "regular"} color={on ? RESTR_TINT : IDLE} style={{ flexShrink: 0, transition: "color 0.2s" }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: on ? RESTR_TINT : "var(--c-text)", transition: "color 0.2s" }}>{label}</div>
                    <div style={{ fontSize: 12, color: "var(--c-text-soft)", marginTop: 1 }}>{desc}</div>
                  </div>
                  <span aria-hidden style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0, boxSizing: "border-box", border: on ? `6px solid ${RESTR_TINT}` : "2px solid #c7c7cc", transition: "border 0.2s" }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* autoComplete off: a habit name is never a saved credential/address,
          but the browser offers its autofill strip (passwords/cards/
          addresses) above the keyboard for any plain text field — which
          lands between this panel and the keyboard and reads as a gap. */}
      {/* Emoji or icon picker beside the name, same as the category form: each
          picker ends with a "use icon" / "use emoji" link that swaps to the other.
          `_iconMode` / `_openPicker` are UI-only (stripped by cleanHabitForm). */}
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {(f._iconMode ?? !!f.phosphorIcon)
          ? <HabitIconPicker value={f.phosphorIcon || ""} color={categories.find(c => c.id === f.goalId)?.color || f.color || "#d4789a"} onChange={icon => setF(p => ({ ...p, phosphorIcon: icon }))} onSwitch={() => setF(p => ({ ...p, _iconMode: false, phosphorIcon: "", _openPicker: true }))} openOnMount={!!f._openPicker} compact />
          : <EmojiPicker value={f.emoji || "⭐"} onChange={e => setF(p => ({ ...p, emoji: e, emojiLocked: true }))} onSwitch={() => setF(p => ({ ...p, _iconMode: true, _openPicker: true }))} openOnMount={!!f._openPicker} compact />}
      <input autoComplete="off" placeholder="Habit name" value={f.name} onChange={e => {
          const name = e.target.value;
          const suggested = suggestEmojiFromName(name);
          setF(p => {
            if (p.emojiLocked) return { ...p, name }; // locked — never auto-update
            if (suggested && (p.emoji === "⭐" || p.emoji === suggestEmojiFromName(p.name))) return { ...p, name, emoji: suggested };
            return { ...p, name };
          });
        }} style={{
          flex: 1, minWidth: 0, border: "none", borderBottom: "2px solid var(--c-border-mid)",
          borderRadius: 0, padding: "8px 2px", fontFamily: "inherit", fontSize: 20,
          fontWeight: 700, outline: "none", background: "transparent", boxSizing: "border-box",
          color: "var(--c-text)",
        }} />
      </div>
      <textarea
        autoComplete="off"
        placeholder="Description (optional)"
        value={f.description || ""}
        onChange={e => setF(p => ({ ...p, description: e.target.value }))}
        rows={2}
        style={{ ...S.input, resize: "none", height: 56, marginTop: 8, fontSize: 14.5 }}
      />
      <div style={{ marginTop: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <p style={{ ...S.label, margin: 0 }}>CATEGORY</p>
          <span style={{ fontSize: 10, color: "var(--c-text-soft)", fontWeight: 600 }}>optional</span>
        </div>
        {/* Category pills — one tap to pick, mirroring the Planner add/edit panel.
            No pill selected leaves the habit under Uncategorized. */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" onClick={() => setF(p => ({ ...p, goalId: "" }))}
            style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", boxShadow: !f.goalId ? "0 0 0 1.5px #a07090, 0 1px 3px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.06)", background: !f.goalId ? "#a0709022" : "var(--c-bg-card)", color: "#a07090", cursor: "pointer", fontFamily: "inherit" }}>
            Uncategorized
          </button>
          {categories.map(c => {
            const selected = f.goalId === c.id;
            // vividDark() rebuilds the category's hue at a fixed rich/dark
            // lightness instead of using its raw picked color — a pale
            // category color (light pink, light yellow, ...) used as-is
            // read as washed-out, low-contrast text/icon on the light card bg.
            // Applied unconditionally (not just when selected) to match this
            // chip's text, which already did this before the icon did.
            const chipColor = categoryInk(c.color || "#d4789a");
            return (
              <button key={c.id} type="button" onClick={() => setF(p => ({ ...p, goalId: c.id }))}
                style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", boxShadow: selected ? `0 0 0 1.5px ${chipColor}, 0 1px 3px rgba(0,0,0,0.06)` : "0 1px 3px rgba(0,0,0,0.06)", background: selected ? (c.color || "#d4789a") + "22" : "var(--c-bg-card)",
                  color: chipColor, cursor: "pointer", fontFamily: "inherit" }}>
                <CategoryIcon cat={c} size={15} color={chipColor} /> {c.title}
              </button>
            );
          })}
          {setShowNewCat && !showNewCat && (
            <button type="button" onClick={() => setShowNewCat(true)}
              style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "1.5px dashed var(--c-border-mid)", background: "transparent", color: "var(--c-text-soft)", cursor: "pointer", fontFamily: "inherit" }}>
              <Plus size={14} /> New category
            </button>
          )}
        </div>
        {setShowNewCat && showNewCat && (
          <InlineCategoryCreator
            categories={categories}
            setCategories={setCategories}
            savedColors={savedColors}
            addSavedColor={addSavedColor}
            S={S}
            onCreated={newCat => { setF(p => ({ ...p, goalId: newCat.id })); setShowNewCat(false); }}
            onCancel={() => setShowNewCat(false)}
          />
        )}
      </div>

      {/* Unit (Count / Steps / Pages / Glasses / Time / Custom, see
          habitUnits.js) and how much of it a day. Count is the original
          "times per day" stepper, and it also controls how many Time of Day
          slots below can be picked (brushing teeth 2x/day → Morning + Evening);
          every other unit is one daily total, so one slot. Only meaningful for
          "todo" habits (Restricted habits use their own allowance system). The
          goal itself is still `dailyCount`, so a unit change goes through the
          same "Start a new goal" switch as a times-per-day change. */}
      {f.habitType === "todo" && (
        <div style={{ marginTop: 12 }}>
          <p style={{ ...S.label, margin: "0 0 6px" }}>UNIT</p>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {HABIT_UNITS.map(u => {
              const selected = unit === u.id;
              return (
                <button key={u.id} type="button" onClick={() => setF(p => {
                  if (unitOf(p) === u.id) return p;
                  // Switching back to the unit the habit had when the form
                  // opened restores its goal, so trying a unit isn't a change.
                  const orig = p._goalOrig;
                  const backToOrig = orig && (orig.unit || "count") === u.id;
                  const dailyCount = backToOrig ? (orig.dailyCount || 1) : defaultGoalFor(u.id);
                  const timeOfDay = u.id === "count" ? p.timeOfDay : (p.timeOfDay ? p.timeOfDay.slice(0, 1) : p.timeOfDay);
                  const unitLabel = u.id === "custom" ? (p.unitLabel || (backToOrig ? orig.unitLabel : "") || "") : "";
                  // Picking Custom opens its unit list right away (inline, below).
                  return { ...p, unit: u.id, dailyCount, timeOfDay, unitLabel, _unitPickerOpen: u.id === "custom" };
                })}
                  // Same text size as the CATEGORY pills above (Jade, 2026-10-11).
                  style={{ fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", whiteSpace: "nowrap", fontFamily: "inherit", cursor: "pointer",
                    background: selected ? "var(--c-bg-active)" : "var(--c-bg-card)",
                    color: selected ? "var(--c-text-accent)" : "var(--c-text)",
                    boxShadow: selected ? "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.06)" }}>
                  {u.label}
                </button>
              );
            })}
          </div>
          {/* Custom unit: one row showing the picked unit; tapping it opens or
              closes the UnitPicker list right below it, inside the form
              (categories + Other; Jade). `_unitPickerOpen` is UI-only. */}
          {unit === "custom" && (
            <button type="button" onClick={() => setF(p => ({ ...p, _unitPickerOpen: !p._unitPickerOpen }))}
              style={{ ...S.btn, marginTop: 10, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", fontFamily: "inherit", cursor: "pointer", background: "var(--c-bg-card)" }}>
              <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)" }}>Custom unit</span>
              {/* The arrow sits in its own fixed box with a clear gap, so turning
                  it (open = pointing down) never crowds the unit name (Jade). */}
              <span style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text-accent)", display: "flex", alignItems: "center", gap: 10 }}>
                {String(f.unitLabel || "").trim() || "Choose"}
                <span style={{ width: 14, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", lineHeight: 1, transition: "transform 0.15s", transform: f._unitPickerOpen ? "rotate(90deg)" : "none" }}>›</span>
              </span>
            </button>
          )}
          {unit === "custom" && f._unitPickerOpen && (
            <UnitPicker value={f.unitLabel}
              onPick={(pick) => setF(p => ({ ...p, ...pick, _unitPickerOpen: false }))} />
          )}
          {unit === "count" ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
              <button onClick={() => setF(p => {
                const next = Math.max(1, (p.dailyCount||1) - 1);
                const trimmedTimeOfDay = (p.timeOfDay || []).slice(0, next);
                return { ...p, dailyCount: next, timeOfDay: trimmedTimeOfDay };
              })}
                style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
              <span style={{ fontSize: 18, fontWeight: 700, color: "var(--c-text-accent)", minWidth: 24, textAlign: "center" }}>{f.dailyCount || 1}</span>
              <button onClick={() => setF(p => {
                const next = Math.min(COUNT_MAX, (p.dailyCount||1) + 1);
                return { ...p, dailyCount: next };
              })}
                style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)", color: "var(--c-text-accent)", borderColor: "var(--c-primary)" }}>+</button>
              <span style={{ fontSize: 12, color: "#000000" }}>{(f.dailyCount||1) === 1 ? "once a day" : `${f.dailyCount}x a day`}</span>
            </div>
          ) : unit === "time" ? (
            <TimeGoalInput minutes={f.dailyCount} onChange={(m) => setF(p => ({ ...p, dailyCount: m }))} />
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {unit === "custom" && customPreset(f.unitLabel)?.prefix && (
                <span style={{ fontSize: 16, fontWeight: 700, color: "var(--c-text)" }}>{f.unitLabel}</span>
              )}
              <input type="number" inputMode={unitDef(unit).decimals ? "decimal" : "numeric"} min={unitDef(unit).decimals ? 0.1 : 1} max={unitDef(unit).max}
                step={unitDef(unit).decimals ? 0.1 : 1} value={f.dailyCount ?? ""} aria-label="Daily goal"
                onChange={e => { const v = e.target.value; setF(p => ({ ...p, dailyCount: v === "" ? "" : Number(v) })); }}
                style={{ ...S.input, width: 100, fontSize: 16, fontWeight: 700, padding: "7px 10px", textAlign: "center", color: "var(--c-text-accent)" }} />
              {!(unit === "custom" && customPreset(f.unitLabel)?.prefix) && (
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--c-text)" }}>{unit === "custom" ? (String(f.unitLabel || "").trim() || "units") : unitDef(unit).many}</span>
              )}
            </div>
          )}
          {goalToggleFor("daily", ["dailyCount", "unit", "unitLabel"])}
        </div>
      )}

      {isTodo && (
      <div style={{ marginTop: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
          <p style={{ ...S.label, margin: 0 }}>TIME OF DAY</p>
          <span style={{ fontSize: 10, color: "var(--c-text-soft)", fontWeight: 600 }}>optional</span>
          {f.timeOfDay == null && (
            <span style={{ fontSize: 10, color: "#e67e22", fontWeight: 700, background: "#e67e2222", borderRadius: 8, padding: "2px 7px" }}>Not set</span>
          )}
        </div>
        {/* Multi-select pills — how many can be picked is capped by "Times per
            day" above: a once-a-day habit is single-select (picking a new one
            swaps out the old), while a 2+/day habit can spread across that
            many slots (e.g. brushing teeth 2x/day → Morning + Evening). Used
            to power the habits page's "By Time" view; leaving all unpicked
            just omits the habit from every time-of-day bucket there. */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { key: "morning",   label: "🌅 Morning" },
            { key: "afternoon", label: "☀️ Afternoon" },
            { key: "evening",   label: "🌙 Evening" },
          ].map(({ key, label }) => {
            const selected = (f.timeOfDay || []).includes(key);
            return (
              <button key={key} type="button"
                onClick={() => setF(p => {
                  const cur = p.timeOfDay || [];
                  if (cur.includes(key)) return { ...p, timeOfDay: cur.filter(x => x !== key) };
                  if (maxTimeOfDay <= 1) return { ...p, timeOfDay: [key] }; // single-select: swap
                  if (cur.length >= maxTimeOfDay) return p; // at cap — ignore
                  return { ...p, timeOfDay: [...cur, key] };
                })}
                style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, flex: "1 1 100px", fontSize: 14.5, fontWeight: 700, padding: "6px 13px", borderRadius: 20, border: "none", whiteSpace: "nowrap", boxShadow: selected ? "0 0 0 1.5px var(--c-primary), 0 1px 3px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.06)", background: selected ? "var(--c-bg-active)" : "var(--c-bg-card)", color: selected ? "var(--c-text-accent)" : "var(--c-text-mid)", cursor: "pointer", fontFamily: "inherit" }}>
                {label}
              </button>
            );
          })}
        </div>
      </div>
      )}

      {/* Reminder — optional. A habit has no due timestamp of its own, so
          reminderTime doubles as that anchor: the offsets below (same presets
          as tasks/events) count backwards from it. Day/Week-before is
          skipped here — a habit isn't strictly assigned to one specific day
          the way a task/event is, so "N days before" has no clear anchor.
          Todo habits only — restricted habits are about resisting, not
          "doing". */}
      {isTodo && Capacitor.isNativePlatform() && (() => {
        const hasReminder = !!f.reminderTime;
        // Counted habits (X times a week/month/year) have no due dates, so a
        // reminder on one can't mean "every day it's due". It's armed for the
        // single day it was set on instead — reminderDate, stamped below and
        // read by isHabitDueForReminderOn.
        const counted = isCountFrequency(f);
        const chip = (on) => ({ padding: "5px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: "pointer", border: on ? "none" : "1px solid var(--c-border)", color: on ? "#fff" : "var(--c-text-mid)", background: on ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-bg-card)" });
        return (
        <div style={{ marginTop: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 10 }}>
            <p style={{ ...S.label, margin: 0 }}>REMINDER</p>
            <span style={{ fontSize: 10, color: "var(--c-text-soft)", fontWeight: 600 }}>optional</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <NativePickerInput type="time" value={f.reminderTime || ""} onChange={e => setF(p => ({
                ...p,
                reminderTime: e.target.value || "",
                // Stamped on every change, for every habit type, so a habit
                // later switched to a counted frequency can't inherit a stale
                // armed day and ring out of nowhere. Only counted habits
                // actually read it.
                reminderDate: e.target.value ? todayStr() : "",
                reminderOffsets: p.reminderOffsets && p.reminderOffsets.length ? p.reminderOffsets : [0],
              }))}
              style={{ ...S.input, width: 130, flex: "0 0 auto" }} />
            {hasReminder && (
              <button type="button" onClick={() => { stopAlarmSound(); setF(p => ({ ...p, reminderTime: "", reminderDate: "" })); }} style={{ ...chip(false), display: "flex", alignItems: "center", gap: 4 }}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
          {hasReminder && (
            <div style={{ marginTop: 10 }}>
              <ReminderPicker
                value={f.reminderOffsets && f.reminderOffsets.length ? f.reminderOffsets : [0]}
                onChange={arr => setF(p => ({ ...p, reminderOffsets: arr.length ? arr : [0] }))}
                alertType={f.reminderType === "alarm" ? "alarm" : "notification"}
                onAlertTypeChange={type => setF(p => ({ ...p, reminderType: type }))}
                alarmSound={f.alarmSound || DEFAULT_ALARM_SOUND}
                onAlarmSoundChange={id => setF(p => ({ ...p, alarmSound: id }))}
                alarmSoundUri={f.alarmSoundUri}
                alarmSoundLabel={f.alarmSoundLabel}
                onCustomSoundChange={picked => setF(p => picked ? { ...p, alarmSoundUri: picked.uri, alarmSoundLabel: picked.label } : { ...p, alarmSoundUri: "", alarmSoundLabel: "" })}
                hideDayWeek
              />
              {counted && (
                <p style={{ fontSize: 11, color: "var(--c-text-mid)", margin: "8px 0 0" }}>
                  Counted habits ring once, same day only (on the day you set it), then clear themselves automatically. You can set a new time whenever you want.
                </p>
              )}
            </div>
          )}
        </div>
        );
      })()}

      {/* Add to Clock-In — opt-out: on by default so nothing existing disappears.
          Short habits (e.g. "take vitamins") don't need to sit in the focus queue. */}
      <div onClick={() => setF(p => ({ ...p, addToClockIn: p.addToClockIn === false }))}
        style={{ display: "flex", alignItems: "center", gap: 9, marginTop: 12, padding: "9px 12px", background: "var(--c-bg-card)", border: "none", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", borderRadius: 12, cursor: "pointer" }}>
        <HabitCheckbox checked={f.addToClockIn !== false} color="var(--c-primary)" size={18} burst={false}
          onChange={() => setF(p => ({ ...p, addToClockIn: p.addToClockIn === false }))} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: "var(--c-text)" }}>Add to Clock-In</div>
        </div>
      </div>

      {/* Start Date — picker indicator is NativePickerInput's own chevron */}
      <p style={{ ...S.label, marginTop: 10 }}>START DATE</p>
      <div style={{ position: "relative" }}>
        <NativePickerInput type="date" value={f.habitStartDate || ""} onChange={e => setF(p => ({ ...p, habitStartDate: e.target.value }))}
          style={{ ...S.input, fontSize: 14.5, paddingRight: 26, borderColor: f.habitStartDate ? "var(--c-primary)" : undefined, color: f.habitStartDate ? undefined : "transparent" }} />
        {/* The native mm/dd/yyyy placeholder is hidden (color:transparent above)
            so this "Today" overlay reads cleanly instead of the two overlapping. */}
        {!f.habitStartDate && (
          <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14.5, color: "var(--c-text)", pointerEvents: "none" }}>Today</span>
        )}
      </div>

      {/* End Date */}
      <p style={{ ...S.label, marginTop: 10 }}>END DATE <span style={{ fontSize: 10, color: "#000000", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(optional)</span></p>
      <NativePickerInput type="date" value={f.endDate || ""} onChange={e => setF(p => ({ ...p, endDate: e.target.value }))}
        style={{ ...S.input, fontSize: 14.5, paddingRight: 26, borderColor: f.endDate ? "#a040c8" : undefined }} />
      {f.endDate && (
        <p style={{ fontSize: 11, color: "#a040c8", margin: "4px 0 0", display: "flex", alignItems: "center", gap: 4 }}>
          🏁 This habit ends on {f.endDate}
        </p>
      )}

      {/* Excluded dates — one-off days off (holidays, birthdays). Hidden for
          counted habits ("X times a week/month/year"), which have no fixed
          day to exclude. The picker always shows empty: picking a date adds
          it as a chip (tap × to remove). See isHabitExcludedOn. */}
      {hasExcludableDates(f) && (() => {
        const list = Array.isArray(f.excludedDates) ? f.excludedDates : [];
        const fmtChip = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        return (<>
          <p style={{ ...S.label, marginTop: 10 }}>EXCLUDED DATES <span style={{ fontSize: 10, color: "#000000", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(optional)</span></p>
          <div style={{ position: "relative" }}>
            <NativePickerInput type="date" value=""
              // A year typed one digit at a time on desktop passes through
              // valid-looking dates like 0002-12-25; only real years are added.
              onChange={e => { const v = e.target.value; if (/^(19|20)\d\d-\d\d-\d\d$/.test(v)) setF(p => ({ ...p, excludedDates: [...new Set([...(p.excludedDates || []), v])].sort() })); }}
              style={{ ...S.input, fontSize: 14.5, paddingRight: 26, color: "transparent" }} />
            <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 14.5, color: "var(--c-text-soft)", pointerEvents: "none" }}>+ Add a date</span>
          </div>
          {list.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
              {list.map(ds => (
                <span key={ds} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "var(--c-text)", background: "var(--c-bg-card)", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderRadius: 99, padding: "4px 6px 4px 10px" }}>
                  {fmtChip(ds)}
                  <button type="button" aria-label={`Remove ${fmtChip(ds)}`} onClick={() => setF(p => ({ ...p, excludedDates: toggleExcludedDate(p.excludedDates, ds) }))}
                    style={{ background: "none", border: "none", padding: 0, margin: 0, width: 18, height: 18, minWidth: 0, minHeight: 0, boxShadow: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--c-text-soft)" }}><X size={13} /></button>
                </span>
              ))}
            </div>
          )}
        </>);
      })()}

      {f.habitType === "todo" && (<>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
          <p style={{ ...S.label, margin: 0 }}>FREQUENCY</p>
          {!f.frequencyType && (
            <span style={{ fontSize: 10, color: "#e67e22", fontWeight: 700, background: "#e67e2222", borderRadius: 8, padding: "2px 7px" }}>Not set</span>
          )}
        </div>

        {/* Top-level: 5 pill options, wrapping onto two rows like the Planner
            panel's FREQUENCY (Jade, 2026-10-16: one row of five was tight). */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {[
            { val: "daily",    label: "Daily"   },
            { val: "specific", label: "Weekly"  },
            { val: "monthly",  label: "Monthly"  },
            { val: "yearly",   label: "Yearly"  },
            { val: "interval", label: "Interval" },
          ].map(({ val, label }) => {
            const active = f.frequencyType === val || (f.frequencyType === "count" && val === "specific");
            return (
            <button key={val} onClick={() => setF(p => {
              const wasCount = p.frequencyType === "count" || (p.frequencyType === "monthly" && p.monthlyMode === "count");
              if (val === "specific") return { ...p, frequencyType: wasCount ? "count" : "specific" };
              if (val === "monthly") return { ...p, frequencyType: "monthly", monthlyMode: wasCount ? "count" : "dates" };
              return { ...p, frequencyType: val };
            })}
              style={{ ...S.btn, flex: "1 1 88px", minHeight: 0, padding: "9px 10px", fontSize: 14.5, whiteSpace: "nowrap", fontWeight: active ? 700 : 500, background: active ? "var(--c-bg-card)" : "transparent", color: active ? "var(--c-primary)" : "#a07090", borderColor: active ? "var(--c-primary)" : undefined }}>
              {label}
            </button>
          );})}
        </div>

        {/* Interval: every N days / weeks / months, counted from the start date
            (Jade, 2026-10-15). Rules in src/utils/habitInterval.js. */}
        {f.frequencyType === "interval" && (
          <IntervalPicker value={f} anchor={f.habitStartDate || f.intervalStart || todayStr()} btnStyle={S.btn}
            onChange={v => setF(p => ({ ...p, ...v }))} />
        )}

        {/* Weekly expanded panel — sub-toggle: Specific days vs Times/week */}
        {(f.frequencyType === "specific" || f.frequencyType === "count") && (
          <div style={{ marginTop: 10, background: "var(--c-bg-card)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            {/* Sub-mode toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <button onClick={() => setF(p => ({ ...p, frequencyType: "specific" }))}
                style={{ ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", fontWeight: f.frequencyType === "specific" ? 700 : 400, background: f.frequencyType === "specific" ? "var(--c-bg-card)" : "transparent", color: f.frequencyType === "specific" ? "var(--c-primary)" : "#a07090", borderColor: f.frequencyType === "specific" ? "var(--c-primary)" : undefined }}>
                📆 Specific days
              </button>
              <button onClick={() => setF(p => ({ ...p, frequencyType: "count" }))}
                style={{ ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", fontWeight: f.frequencyType === "count" ? 700 : 400, background: f.frequencyType === "count" ? "var(--c-bg-card)" : "transparent", color: f.frequencyType === "count" ? "var(--c-primary)" : "#a07090", borderColor: f.frequencyType === "count" ? "var(--c-primary)" : undefined }}>
                🔢 Times/week
              </button>
            </div>

            {/* Specific days picker */}
            {f.frequencyType === "specific" && (
              <>
                <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>Which days?</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                  {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                    <button key={d} onClick={() => setF(p => ({ ...p, dayLabels: "named", days: p.days.includes(i) ? p.days.filter(x => x !== i) : [...p.days, i] }))}
                      style={{ ...S.btn, fontSize: 11, padding: "8px 2px", minWidth: 0, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip", fontWeight: f.days.includes(i) ? 800 : 400, background: f.days.includes(i) ? "var(--c-bg-active)" : "transparent", color: f.days.includes(i) ? "var(--c-primary)" : "#a07090", borderColor: f.days.includes(i) ? "var(--c-primary)" : undefined }}>{d}</button>
                  ))}
                </div>
                {goalToggleFor("freq", ["days"])}
              </>
            )}

            {/* Times/week picker */}
            {f.frequencyType === "count" && (
              <>
                <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>How many times per week?</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setF(p => ({ ...p, weeklyCount: Math.max(1, (p.weeklyCount || 3) - 1) }))}
                    style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                  <span style={{ fontSize: 20, fontWeight: 700, color: "var(--c-text-accent)", minWidth: 28, textAlign: "center" }}>{f.weeklyCount || 3}</span>
                  <button onClick={() => setF(p => ({ ...p, weeklyCount: Math.min(7, (p.weeklyCount || 3) + 1) }))}
                    style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)", color: "var(--c-text-accent)", borderColor: "var(--c-primary)" }}>+</button>
                  <span style={{ fontSize: 12, color: "#000000" }}>times per week</span>
                </div>
                {goalToggleFor("freq", ["weeklyCount"])}
              </>
            )}
          </div>
        )}

        {/* Monthly */}
        {f.frequencyType === "monthly" && (
          <div style={{ marginTop: 10, background: "var(--c-bg-card)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            {/* Monthly sub-mode toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <button onClick={() => setF(p => ({ ...p, monthlyMode: "dates" }))}
                style={{ ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", fontWeight: (f.monthlyMode || "dates") === "dates" ? 700 : 400, background: (f.monthlyMode || "dates") === "dates" ? "var(--c-bg-card)" : "transparent", color: (f.monthlyMode || "dates") === "dates" ? "var(--c-primary)" : "#a07090", borderColor: (f.monthlyMode || "dates") === "dates" ? "var(--c-primary)" : undefined }}>
                📅 Specific dates
              </button>
              <button onClick={() => setF(p => ({ ...p, monthlyMode: "count" }))}
                style={{ ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", fontWeight: f.monthlyMode === "count" ? 700 : 400, background: f.monthlyMode === "count" ? "var(--c-bg-card)" : "transparent", color: f.monthlyMode === "count" ? "var(--c-primary)" : "#a07090", borderColor: f.monthlyMode === "count" ? "var(--c-primary)" : undefined }}>
                🔢 Times/month
              </button>
            </div>

            {(f.monthlyMode || "dates") === "dates" && (
              <>
                <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>Pick dates of the month:</p>
                <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  {Array.from({ length: 31 }, (_, i) => (
                    <button key={i+1} onClick={() => setF(p => ({ ...p, monthDates: p.monthDates.includes(i+1) ? p.monthDates.filter(x => x !== i+1) : [...p.monthDates, i+1] }))}
                      style={{ ...S.btn, fontSize: 11, width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: f.monthDates.includes(i+1) ? 800 : 400, background: f.monthDates.includes(i+1) ? "var(--c-bg-active)" : "transparent", color: f.monthDates.includes(i+1) ? "var(--c-primary)" : "#a07090", borderColor: f.monthDates.includes(i+1) ? "var(--c-primary)" : undefined }}>{i+1}</button>
                  ))}
                </div>
                {goalToggleFor("freq", ["monthDates"])}
              </>
            )}

            {f.monthlyMode === "count" && (
              <>
                <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>How many times this month?</p>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <button onClick={() => setF(p => ({ ...p, monthlyCount: Math.max(1, (p.monthlyCount || 10) - 1) }))}
                    style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                  <span style={{ fontSize: 20, fontWeight: 700, color: "var(--c-text-accent)", minWidth: 28, textAlign: "center" }}>{f.monthlyCount || 10}</span>
                  <button onClick={() => setF(p => ({ ...p, monthlyCount: Math.min(31, (p.monthlyCount || 10) + 1) }))}
                    style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-card)", color: "var(--c-text-accent)", borderColor: "var(--c-primary)" }}>+</button>
                  <span style={{ fontSize: 12, color: "#000000" }}>times per month</span>
                </div>
                {goalToggleFor("freq", ["monthlyCount"])}
              </>
            )}
          </div>
        )}

        {/* Yearly */}
        {f.frequencyType === "yearly" && (() => {
          // Defaults to "dates" (free) rather than "count" (Premium, gated below) —
          // picking "Yearly" shouldn't silently land a free user on the paid sub-mode.
          const ymode = f.yearlyMode || "dates";
          const yDates = f.yearDates || [];
          const MSHORT = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
          const [ypMonth] = [f.yearPickMonth ?? now().getMonth()];
          return (
          <div style={{ marginTop: 10, background: "var(--c-bg-card)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            {/* Yearly sub-mode toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <button onClick={() => setF(p => ({ ...p, yearlyMode: "dates" }))}
                style={{ ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", fontWeight: ymode === "dates" ? 700 : 400, background: ymode === "dates" ? "var(--c-bg-card)" : "transparent", color: ymode === "dates" ? "var(--c-primary)" : "#a07090", borderColor: ymode === "dates" ? "var(--c-primary)" : undefined }}>
                📅 Specific dates
              </button>
              <button onClick={() => setF(p => ({ ...p, yearlyMode: "count" }))}
                style={{ ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", fontWeight: ymode === "count" ? 700 : 400, background: ymode === "count" ? "var(--c-bg-card)" : "transparent", color: ymode === "count" ? "var(--c-primary)" : "#a07090", borderColor: ymode === "count" ? "var(--c-primary)" : undefined }}>
                🔢 Times/year
              </button>
            </div>

            {/* Specific calendar dates picker */}
            {ymode === "dates" && (<>
              <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>Pick the calendar dates you'll do this habit:</p>
              {/* Month selector */}
              <div style={{ display: "flex", gap: 3, flexWrap: "wrap", marginBottom: 8 }}>
                {MSHORT.map((m, mi) => (
                  <button key={m} onClick={() => setF(p => ({ ...p, yearPickMonth: mi }))}
                    style={{ ...S.btn, fontSize: 10, padding: "4px 7px", fontWeight: ypMonth === mi ? 700 : 400, background: ypMonth === mi ? "var(--c-bg-active)" : "transparent", color: ypMonth === mi ? "var(--c-primary)" : "#a07090" }}>{m}</button>
                ))}
              </div>
              {/* Day grid for selected month */}
              <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                {Array.from({ length: daysInMonth(2024, ypMonth) }, (_, i) => {
                  const key = `${ypMonth + 1}-${i + 1}`;
                  const on = yDates.includes(key);
                  return (
                    <button key={i+1} onClick={() => setF(p => { const cur = p.yearDates || []; return { ...p, yearDates: cur.includes(key) ? cur.filter(x => x !== key) : [...cur, key] }; })}
                      style={{ ...S.btn, fontSize: 11, width: 30, height: 30, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: on ? 800 : 400, background: on ? "var(--c-bg-active)" : "transparent", color: on ? "var(--c-primary)" : "#a07090", borderColor: on ? "var(--c-primary)" : undefined }}>{i+1}</button>
                  );
                })}
              </div>
              {yDates.length > 0 && (
                <p style={{ fontSize: 10, color: "var(--c-text-accent)", margin: "8px 0 0", fontWeight: 600 }}>
                  {yDates.length} date{yDates.length > 1 ? "s" : ""} selected: {[...yDates].sort((a,b) => { const [am,ad]=a.split("-").map(Number); const [bm,bd]=b.split("-").map(Number); return am-bm || ad-bd; }).map(k => { const [m,d] = k.split("-").map(Number); return `${MSHORT[m-1]} ${d}`; }).join(", ")}
                </p>
              )}
              {goalToggleFor("freq", ["yearDates"])}
            </>)}

            {/* Times/year — counted (type a number) */}
            {ymode === "count" && (<>
              <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>How many times this year? Due {365} days from the start date.</p>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="number" inputMode="numeric" min={1} max={365} value={f.yearlyCount ?? 12}
                  onChange={e => { const v = e.target.value; setF(p => ({ ...p, yearlyCount: v === "" ? "" : Math.max(1, Math.min(365, parseInt(v, 10) || 1)) })); }}
                  style={{ ...S.input, width: 90, textAlign: "center", fontSize: 16, fontWeight: 700, color: "var(--c-text-accent)" }} />
                <span style={{ fontSize: 12, color: "#000000" }}>times per year</span>
              </div>
              {goalToggleFor("freq", ["yearlyCount"])}
            </>)}
          </div>
          );
        })()}

      </>)}

      {f.habitType === "limited" && (() => {
        const rPeriod = (f.allowanceMode === "month" || f.allowanceMode === "monthDates") ? "month" : (f.limitedInterval && !f.allowanceMode) ? "interval" : "week";
        const rBtn = (active) => ({
          ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", textAlign: "center",
          fontWeight: active ? 700 : 400,
          background: active ? "var(--c-bg-card)" : "transparent",
          color: active ? "var(--c-primary)" : "#a07090",
          borderColor: active ? "var(--c-primary)" : undefined,
        });
        const rBtnWarm = (active) => ({
          ...S.btn, flex: 1, fontSize: 14.5, padding: "9px 4px", whiteSpace: "nowrap", textAlign: "center",
          fontWeight: active ? 700 : 400,
          background: active ? "#fef3e2" : "transparent",
          color: active ? "#c27d20" : "#a07090",
          borderColor: active ? "#f5c842" : undefined,
        });
        // Switching the top-level period should keep whichever sub-mode
        // ("times per period" count vs. "specific days") the user was
        // already in — otherwise flipping Week<->Month silently dumps them
        // into "specific days" and their times-per-period number/edit is lost.
        const switchPeriod = (period) => {
          // Interval (Jade, 2026-10-17): the not-allowed days repeat every N
          // days / weeks / months from the start date (isLimitedRestrictedOn).
          if (period === "interval") { setF(p => ({ ...p, allowanceMode: "", limitedInterval: true })); return; }
          if (f.limitedInterval) setF(p => ({ ...p, limitedInterval: false }));
          const wasCount = f.allowanceMode === "week" || f.allowanceMode === "month";
          if (wasCount) setF(p => {
            const cap = period === "week" ? 7 : 31;
            return { ...p, allowanceMode: period, allowanceMax: Math.min(cap, Math.max(1, p.allowanceMax || 2)) };
          });
          else setF(p => ({ ...p, allowanceMode: period === "week" ? "" : "monthDates" }));
        };
        return (
          <>
            <p style={{ ...S.label, marginTop: 10 }}>FREQUENCY</p>
            {/* Top-level: Per Week | Per Month */}
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => switchPeriod("week")}
                style={rBtn(rPeriod === "week")}>Per Week</button>
              <button onClick={() => switchPeriod("month")}
                style={rBtn(rPeriod === "month")}>Per Month</button>
              <button onClick={() => switchPeriod("interval")}
                style={rBtn(rPeriod === "interval")}>Interval</button>
            </div>

            {/* ── Interval: not allowed every N days / weeks / months ── */}
            {rPeriod === "interval" && (<>
              <IntervalPicker value={f} anchor={f.habitStartDate || f.intervalStart || todayStr()} btnStyle={S.btn}
                onChange={v => setF(p => ({ ...p, ...v }))} />
              <p style={{ fontSize: 11, color: "var(--c-text-soft)", margin: "6px 2px 0" }}>These are the days this habit is <strong>not allowed</strong>.</p>
            </>)}

            {/* ── Per Week expanded panel ── */}
            {rPeriod === "week" && (
              <div style={{ marginTop: 10, background: "var(--c-bg-card)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  <button onClick={() => setF(p => ({ ...p, allowanceMode: "" }))}
                    style={rBtn(f.allowanceMode === "")}>📆 Specific days</button>
                  <button onClick={() => setF(p => ({ ...p, allowanceMode: "week" }))}
                    style={rBtn(f.allowanceMode === "week")}>🔢 Times/week</button>
                </div>

                {/* Specific days — limited: which days NOT allowed (restricted) */}
                {f.allowanceMode === "" && (() => { const offSel = limitedOffDays(f); return (<>
                  <p style={{ fontSize: 11, color: "#000000", margin: "0 0 6px" }}>Which days is this habit <strong>not allowed</strong>?</p>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 5 }}>
                    {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
                      <button key={d} onClick={() => setF(p => { const cur = limitedOffDays(p); return { ...p, limitedOffDays: cur.includes(i) ? cur.filter(x => x !== i) : [...cur, i], allowedDays: undefined }; })}
                        style={{ ...S.btn, fontSize: 11, padding: "8px 2px", minWidth: 0, textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "clip", fontWeight: offSel.includes(i) ? 800 : 400, background: offSel.includes(i) ? "var(--c-bg-active)" : "transparent", color: offSel.includes(i) ? "var(--c-primary)" : "#a07090", borderColor: offSel.includes(i) ? "var(--c-primary)" : undefined }}>{d}</button>
                    ))}
                  </div>
                </>); })()}

                {/* Times/week — allowance budget */}
                {f.allowanceMode === "week" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => setF(p => ({ ...p, allowanceMax: Math.max(1, (p.allowanceMax||2) - 1) }))}
                      style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#c27d20", minWidth: 28, textAlign: "center" }}>{f.allowanceMax || 2}</span>
                    <button onClick={() => setF(p => ({ ...p, allowanceMax: Math.min(7, (p.allowanceMax||2) + 1) }))}
                      style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-soft)", color: "#c27d20", borderColor: "#f5c842" }}>+</button>
                    <span style={{ fontSize: 12, color: "#000000" }}>times per week. Going over = a slip</span>
                  </div>
                )}
              </div>
            )}

            {/* ── Per Month expanded panel ── */}
            {rPeriod === "month" && (
              <div style={{ marginTop: 10, background: "var(--c-bg-card)", borderRadius: 10, padding: "10px 12px", border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  <button onClick={() => setF(p => ({ ...p, allowanceMode: "monthDates" }))}
                    style={rBtn(f.allowanceMode === "monthDates")}>📅 Specific dates</button>
                  <button onClick={() => setF(p => ({ ...p, allowanceMode: "month" }))}
                    style={rBtn(f.allowanceMode === "month")}>🔢 Times/month</button>
                </div>

                {/* Specific monthly dates picker */}
                {f.allowanceMode === "monthDates" && (<>
                  <p style={{ fontSize: 11, color: "#000000", margin: "0 0 8px" }}>Which dates of the month is this habit <strong>not allowed</strong>?</p>
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {Array.from({ length: 31 }, (_, i) => (
                      <button key={i+1} onClick={() => setF(p => ({ ...p, restrictedMonthDates: (p.restrictedMonthDates||[]).includes(i+1) ? (p.restrictedMonthDates||[]).filter(x => x !== i+1) : [...(p.restrictedMonthDates||[]), i+1] }))}
                        style={{ ...S.btn, fontSize: 11, width: 32, height: 32, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: (f.restrictedMonthDates||[]).includes(i+1) ? 800 : 400, background: (f.restrictedMonthDates||[]).includes(i+1) ? "var(--c-bg-active)" : "transparent", color: (f.restrictedMonthDates||[]).includes(i+1) ? "var(--c-primary)" : "#a07090", borderColor: (f.restrictedMonthDates||[]).includes(i+1) ? "var(--c-primary)" : undefined }}>{i+1}</button>
                    ))}
                  </div>
                </>)}

                {/* Times/month — allowance budget */}
                {f.allowanceMode === "month" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button onClick={() => setF(p => ({ ...p, allowanceMax: Math.max(1, (p.allowanceMax||2) - 1) }))}
                      style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>−</button>
                    <span style={{ fontSize: 20, fontWeight: 700, color: "#c27d20", minWidth: 28, textAlign: "center" }}>{f.allowanceMax || 2}</span>
                    <button onClick={() => setF(p => ({ ...p, allowanceMax: Math.min(31, (p.allowanceMax||2) + 1) }))}
                      style={{ ...S.btn, width: 30, height: 30, fontSize: 18, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, background: "var(--c-bg-soft)", color: "#c27d20", borderColor: "#f5c842" }}>+</button>
                    <span style={{ fontSize: 12, color: "#000000" }}>times per month. Going over = a slip</span>
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {f.habitType === "nottodo" && (<>
        <p style={{ ...S.label, marginTop: 10 }}>WHY I'M QUITTING <span style={{ fontSize: 10, color: "#000000", fontWeight: 500, textTransform: "none", letterSpacing: 0 }}>(optional)</span></p>
        <textarea rows={2} value={f.quitReason} onChange={e => setF(p => ({ ...p, quitReason: e.target.value }))} style={{ ...S.input, resize: "none", height: 56 }} />
      </>)}

      {/* Show on daily list toggle — only for yearly-counted habits (others'
          daily-list visibility depends on the day, so no toggle is offered). */}
      {isHabitCountedYearly(f) && (() => {
        const shown = !isHabitHiddenFromDaily(f);
        return (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12, padding: "10px 12px", background: "var(--c-bg-card)", borderRadius: 10, border: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "var(--c-text)" }}>Show on daily list</div>
            </div>
            <button onClick={() => setF(p => ({ ...p, showOnDailyList: isHabitHiddenFromDaily(p) }))}
              aria-label="Toggle show on daily list"
              style={{ flexShrink: 0, minWidth: 0, minHeight: 0, width: 38, height: 22, borderRadius: 999, border: "none", cursor: "pointer", padding: 0, position: "relative", background: shown ? "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))" : "var(--c-border)", transition: "background 0.2s" }}>
              <span style={{ position: "absolute", top: 2, left: shown ? 18 : 2, width: 18, height: 18, borderRadius: "50%", background: "var(--c-bg-card)", transition: "left 0.2s cubic-bezier(0.34,1.56,0.64,1)", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
            </button>
          </div>
        );
      })()}

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button onClick={onSave} className="clarity-add-btn"
          disabled={!f.name.trim() || saving}
          style={{ ...S.addBtn, opacity: !f.name.trim() ? 0.45 : 1, cursor: !f.name.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
          {saving ? <><CheckCircle size={16} />Saved!</> : saveLabel}
        </button>
        <button onClick={onCancel} style={S.btn}><X size={14} style={{marginRight:4}} />Cancel</button>
      </div>
    </>
    );
}

// Hours + minutes for a Time goal; the habit stores the total in minutes
// (dailyCount). Either box may be left empty while typing — the save clamps
// the total to 1 min…24h (normalizeHabitUnit).
function TimeGoalInput({ minutes, onChange }) {
  const total = Number(minutes) || 0;
  const h = Math.floor(total / 60), m = total % 60;
  const box = { ...S.input, width: 64, fontSize: 16, fontWeight: 700, padding: "7px 8px", textAlign: "center", color: "var(--c-text-accent)" };
  const num = (v) => Math.max(0, parseInt(v, 10) || 0);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10 }}>
      <input type="number" inputMode="numeric" min={0} max={24} value={h || (total ? 0 : "")} aria-label="Hours"
        onChange={e => onChange(num(e.target.value) * 60 + m)} style={box} />
      <span style={{ fontSize: 13, fontWeight: 700 }}>h</span>
      <input type="number" inputMode="numeric" min={0} max={59} value={m || (total ? 0 : "")} aria-label="Minutes"
        onChange={e => onChange(h * 60 + Math.min(59, num(e.target.value)))} style={box} />
      <span style={{ fontSize: 13, fontWeight: 700 }}>min</span>
    </div>
  );
}
